import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Eye,
  Edit,
  DollarSign,
  FileText,
  ExternalLink,
  TrendingUp,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bondsAPI, formatCurrency, formatDate, formatCompactCurrency } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { toast } from 'sonner';

import { toNumberSafe } from '../../utils/moneyUtils';

const BondManagement = () => {
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  // Removed approvalResult modal; use toast instead
  const [errorModal, setErrorModal] = useState(null); // { title, message }
  const [rejectionModal, setRejectionModal] = useState(null); // { bond, level }
  const [rejectionReason, setRejectionReason] = useState('');
  const [confirmModal, setConfirmModal] = useState(null); // { bond, level }
  const [deleteModal, setDeleteModal] = useState(null); // { bond }
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  const queryClient = useQueryClient();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Helper function to map display type to API type
  const mapTypeFilterToAPI = (displayType) => {
    const typeMap = {
      'Conventional': 'Fixed Notes',
      'Sukuk': 'Sukuk'
    };
    return typeMap[displayType] || displayType;
  };


  // Fetch bonds from API (with pagination)
  const { data: bondsData, isPending: bondsLoading, error: bondsError } = useQuery({
    queryKey: ['bonds', debouncedSearchTerm, statusFilter, typeFilter, currentPage],
    queryFn: () => bondsAPI.getBonds({
      search: debouncedSearchTerm || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      productType: typeFilter === 'all' ? undefined : mapTypeFilterToAPI(typeFilter),
      page: currentPage,
      limit: PAGE_SIZE
    })
  });

  // Derive bonds and totals from axios response safely
  const respRoot = bondsData?.data?.data ?? bondsData?.data ?? bondsData;
  const bonds = Array.isArray(respRoot?.bonds)
    ? respRoot.bonds
    : Array.isArray(respRoot?.data)
      ? respRoot.data
      : Array.isArray(respRoot?.items)
        ? respRoot.items
        : Array.isArray(respRoot?.docs)
          ? respRoot.docs
          : Array.isArray(respRoot?.results)
            ? respRoot.results
            : Array.isArray(respRoot)
              ? respRoot
              : [];

  const totalBonds = typeof respRoot?.total === 'number'
    ? respRoot.total
    : typeof respRoot?.count === 'number'
      ? respRoot.count
      : typeof respRoot?.pagination?.total === 'number'
        ? respRoot.pagination.total
        : bonds.length;

  const totalPages = Math.max(1, Math.ceil((totalBonds || bonds.length) / PAGE_SIZE));

  // Global summary from backend with local fallbacks
  const summary = respRoot?.summary || {};

  // Date-range active: issueDate <= today <= maturityDate
  const today = new Date();
  const isDateRangeActive = (b) => {
    const issue = b.issueDate ? new Date(b.issueDate) : null;
    const maturity = b.maturityDate ? new Date(b.maturityDate) : null;
    return !!(issue && maturity && today >= issue && today <= maturity);
  };

  const localActiveCount = bonds.filter(isDateRangeActive).length;
  const localInternalPendingCount = bonds.filter(b => ['Pending L1', 'Pending L2', 'Pending Review'].includes(b.status)).length;
  // Fallback issuance should reflect active bonds only (date-range definition)
  const localTotalIssuance = bonds.filter(isDateRangeActive).reduce((sum, b) => sum + toNumberSafe(b.issueAmount), 0);
  const localDraftCount = bonds.filter(b => b.status === 'Draft').length;

  const activeCount = toNumberSafe(summary.activeCount) || localActiveCount;
  const internalPendingCount = toNumberSafe(summary.internalPendingCount) || localInternalPendingCount;
  const totalIssuanceRaw = toNumberSafe(summary.totalIssuance) || localTotalIssuance;
  const draftCount = toNumberSafe(summary.draftCount) || localDraftCount;

  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, typeFilter]);

  const getStatusBadge = (status) => {
    const statusClasses = {
      'Draft': 'badge-secondary',
      'Pending L1': 'badge-warning',
      'Pending L2': 'badge-warning',
      'Pending Review': 'badge-warning',
      'Approved': 'badge-success',
      'Rejected': 'badge-error',
      'Listed': 'badge-success',
      'Active': 'badge-success',
      'Ready for Allocation': 'badge-info',
      'Allocation Uploaded': 'badge-info',
      'Allocation Finalized': 'badge-info',
      'Redemption in Process': 'badge-warning',
      'Redemption Completed': 'badge-success',
      'Burn in Process': 'badge-error',
      'Burn Completed': 'badge-neutral',
      'Matured': 'badge-info'
    };

    const statusIcons = {
      'Draft': '📝',
      'Pending L1': '①',
      'Pending L2': '②',
      'Pending Review': '⏳',
      'Approved': '✅',
      'Rejected': '❌',
      'Listed': '🏛️',
      'Active': '✅',
      'Ready for Allocation': '📋',
      'Allocation Uploaded': '📤',
      'Allocation Finalized': '🎯',
      'Redemption in Process': '🔄',
      'Redemption Completed': '✅',
      'Burn in Process': '🔥',
      'Burn Completed': '⚫',
      'Matured': '🏁'
    };

    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        <span className="mr-1">{statusIcons[status]}</span>
        {status}
      </span>
    );
  };

  // Handle internal approval with modal feedback
  const handleInternalApprove = async (bond, level) => {
    try {
      await bondsAPI.internalApproveBond(bond._id, level, { notes: `${level} approval` });
      // Determine new status after approval
      const newStatus = level === 'L1' ? 'Pending L2' : (level === 'L2' ? 'Pending Review' : '');
      toast.success(`Approved ${level}. Status moved to ${newStatus}.`);
      queryClient.invalidateQueries({ queryKey: ['bonds'] });
    } catch (e) {
      setErrorModal({
        title: `${level} Approval Failed`,
        message: e?.response?.data?.message || `${level} approval failed`
      });
    }
  };

  // Handle delete bond (only for Draft status)
  const handleDeleteBond = async (bond) => {
    try {
      await bondsAPI.deleteBond(bond._id);
      toast.success(`Bond "${bond.bondName}" has been deleted.`);
      queryClient.invalidateQueries({ queryKey: ['bonds'] });
      setDeleteModal(null);
    } catch (e) {
      setErrorModal({
        title: 'Delete Failed',
        message: e?.response?.data?.message || 'Failed to delete bond'
      });
      setDeleteModal(null);
    }
  };

  // Helper to extract user ID safely
  const getUserId = (userRef) => {
    if (!userRef) return null;
    if (typeof userRef === 'string') return userRef;
    return userRef._id || userRef.id || null;
  };

  // Business rule: L2 approver must be different from L1 approver
  const canApproveL2 = (bond) => {
    if (bond.status !== 'Pending L2') return false;
    const l1ApproverId = getUserId(bond.l1ApprovedById || bond.l1ApprovedBy);
    const currentUserId = getUserId(authUser);
    return l1ApproverId !== currentUserId;
  };

  // Handle loading and error states
  if (authLoading || bondsLoading) {
    return <LoadingSpinner size="lg" text="Loading bonds..." />;
  }

  if (bondsError) {
    console.error('Bonds error:', bondsError);
  }


  // Remove local filtering, as backend should handle search/filter/pagination
  const filteredBonds = bonds;



  return (
    <div className="w-full max-w-full min-h-screen overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="w-full max-w-full">
        <div className="max-w-full px-4 mx-auto sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bond Management</h1>
              <p className="mt-2 text-sm text-gray-600">
                View and manage all your bond issuances and their current status
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/issuer/bonds/create')}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Bond
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-full px-4 py-8 mx-auto overflow-hidden sm:px-6 lg:px-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 lg:gap-6">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-primary-100">
                  <TrendingUp className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0 ml-4">
                  <p className="text-sm font-medium truncate text-secondary-600">
                    Active Bonds
                  </p>
                  <p className="text-2xl font-bold">
                    {activeCount}
                  </p>
                  <p className="mt-1 text-xs text-success-600">
                    Total number of active bonds
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-success-100">
                  <DollarSign className="w-6 h-6 text-success-600" />
                </div>
                <div className="flex-1 min-w-0 ml-4">
                  <p className="text-sm font-medium truncate text-secondary-600">
                    Total Issuance
                  </p>
                  <p className="text-2xl font-bold">
                    {totalIssuanceRaw > 0 ? formatCompactCurrency(totalIssuanceRaw) : 'RM 0'}
                  </p>
                  <p className="mt-1 text-xs text-success-600">
                    Summation of total issuance
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bonds Section Header */}
        <div className="mt-8 mb-4">
          <h2 className="text-lg font-medium text-secondary-900">
            Your Bonds ({totalBonds})
          </h2>
        </div>

        {/* Filters and Search */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          {/* Search Card - 60% width (3 out of 5 columns) */}
          <div className="card lg:col-span-3">
            <div className="card-body">
              <div className="relative">
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-dark-gray" />
                <input
                  type="text"
                  placeholder="Search bonds by name or ISIN..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 form-input"
                />
              </div>
            </div>
          </div>

          {/* Status Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Status</option>
                <option value="Draft">Draft</option>
                <option value="Pending L1">Pending L1</option>
                <option value="Pending L2">Pending L2</option>
                <option value="Pending Review">Pending Review</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Type Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Types</option>
                <option value="Conventional">Conventional</option>
                <option value="Sukuk">Sukuk</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bonds Table */}
        <div className="w-full max-w-full mt-4 overflow-hidden card">
          <div className="w-full overflow-x-auto">
            <table className="table min-w-full">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell whitespace-nowrap">Bond Details</th>
                  <th className="table-header-cell whitespace-nowrap">ISIN</th>
                  <th className="table-header-cell whitespace-nowrap">Stock Code</th>
                  <th className="table-header-cell whitespace-nowrap">Type</th>
                  <th className="table-header-cell whitespace-nowrap">Amount</th>
                  <th className="table-header-cell whitespace-nowrap">Rate</th>
                  <th className="table-header-cell whitespace-nowrap">Status</th>
                  <th className="table-header-cell whitespace-nowrap">Approval L1</th>
                  <th className="table-header-cell whitespace-nowrap">Approval L2</th>
                  <th className="table-header-cell whitespace-nowrap">Issue Date</th>
                  <th className="table-header-cell whitespace-nowrap">Maturity Date</th>
                  <th className="table-header-cell whitespace-nowrap">Explorer</th>
                  <th className="table-header-cell whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {filteredBonds.length > 0 ? (
                  filteredBonds.map((bond) => (
                    <tr key={bond._id} className="table-row">
                      <td className="table-cell">
                        <div>
                          <div className="font-medium text-dark-gray">{bond.bondName}</div>
                          <div className="text-sm text-secondary-500">
                            Created: {formatDate(bond.createdAt)}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="font-medium text-dark-gray">
                          {bond.isin || '-'}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="font-medium text-dark-gray">
                          {bond.stockCode || '-'}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-primary">{bond.productType === "Fixed Notes" ? "Conventional" : bond.productType}</span>
                      </td>
                      <td className="table-cell">
                        <div className="font-medium text-dark-gray">
                          {formatCurrency(toNumberSafe(bond.issueAmount))}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="font-medium text-dark-gray">{bond.couponRate}%</div>
                      </td>
                      <td className="table-cell">
                        <div>
                          {getStatusBadge(bond.status)}
                          {bond.statusDescription && (
                            <div className="max-w-xs mt-1 text-xs text-secondary-500">
                              {bond.statusDescription}
                            </div>
                          )}
                          {bond.submittedAt && (
                            <div className="mt-1 text-xs text-secondary-400">
                              Submitted: {formatDate(bond.submittedAt)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        {bond.l1ApprovedBy ? (
                          <div className="text-xs text-gray-700">
                            <div className="font-medium">
                              {bond.l1ApprovedBy?.corporateInfo?.companyName || bond.l1ApprovedBy?.email || '—'}
                            </div>
                            {bond.l1ApprovedAt && <div className="text-gray-500">{formatDate(bond.l1ApprovedAt)}</div>}
                          </div>
                        ) : <span className="text-sm text-gray-400">-</span>}
                      </td>
                      <td className="table-cell">
                        {bond.l2ApprovedBy ? (
                          <div className="text-xs text-gray-700">
                            <div className="font-medium">
                              {bond.l2ApprovedBy?.corporateInfo?.companyName || bond.l2ApprovedBy?.email || '—'}
                            </div>
                            {bond.l2ApprovedAt && <div className="text-gray-500">{formatDate(bond.l2ApprovedAt)}</div>}
                          </div>
                        ) : <span className="text-sm text-gray-400">-</span>}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm">{formatDate(bond.issueDate)}</div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm">{formatDate(bond.maturityDate)}</div>
                      </td>
                      <td className="table-cell">
                        {(() => {
                          const tokenAddress = bond?.tokenDetails?.tokenAddress || bond?.tokenDetails?.contractAddress;
                          const explorerBase = 'https://teleskop.aeris-dev.codedsolution-web3.com/address/';
                          
                          if (!tokenAddress) {
                            return <span className="text-xs text-gray-400" title="Token not yet created">—</span>;
                          }
                          
                          return (
                            <a
                              href={`${explorerBase}${tokenAddress}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-1 transition-colors cursor-pointer text-primary-600 hover:text-primary-700"
                              title="View token on blockchain explorer"
                            >
                              <ExternalLink className="w-4 h-4" />
                              <span className="text-sm">Explorer</span>
                            </a>
                          );
                        })()}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigate(`/issuer/bonds/${bond._id}`)}
                            className="p-1 cursor-pointer text-primary-600 hover:text-primary-700"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {(bond.status === 'Draft' || bond.status === 'Pending L1' || bond.status === 'Pending L2') && (
                            <button
                              onClick={() => navigate(`/issuer/bonds/${bond._id}/edit`)}
                              className="p-1 cursor-pointer text-primary-600 hover:text-secondary-700"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" /> 
                            </button>
                          )}
                          {bond.status === 'Draft' && (
                            <button
                              onClick={() => setDeleteModal({ bond })}
                              className="p-1 cursor-pointer text-error-600 hover:text-error-700"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          {bond.status === 'Pending L1' && (
                            <>
                              <button
                                onClick={() => setConfirmModal({ bond, level: 'L1' })}
                                className="btn-primary btn-sm"
                                title="Approve L1"
                              >Approve L1</button>
                              <button
                                onClick={() => {
                                  setRejectionModal({ bond, level: 'L1' });
                                  setRejectionReason('');
                                }}
                                className="btn-secondary btn-sm"
                                title="Reject"
                              >Reject</button>
                            </>
                          )}
                          {bond.status === 'Pending L2' && (
                            <>
                              {canApproveL2(bond) && (
                                <button
                                  onClick={() => setConfirmModal({ bond, level: 'L2' })}
                                  className="btn-primary btn-sm"
                                  title="Approve L2"
                                >Approve L2</button>
                              )}
                              <button
                                onClick={() => {
                                  setRejectionModal({ bond, level: 'L2' });
                                  setRejectionReason('');
                                }}
                                className="btn-secondary btn-sm"
                                title="Reject"
                              >Reject</button>
                            </>
                          )}
                          {bond.status === 'Active' && (
                            <button
                              onClick={() => navigate(`/issuer/funding?bondId=${bond._id}`)}
                              className="p-1 text-success-600 hover:text-success-700"
                              title="Manage Funding"
                            >
                              <DollarSign className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="table-cell py-12 text-center">
                      <div className="text-secondary-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-secondary-300" />
                        <p className="mb-2 text-lg font-medium">
                          {searchTerm ? 'No bonds found' : 'No bonds created yet'}
                        </p>
                        <p className="mb-4 text-sm">
                          {searchTerm
                            ? `No bonds match your search "${searchTerm}"`
                            : 'Create your first bond to get started with issuance'
                          }
                        </p>
                        {!searchTerm && (
                          <button
                            onClick={() => navigate('/issuer/bonds/create')}
                            className="btn-primary"
                          >
                            Create First Bond
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls (below table) */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 mt-4">
            <div className="text-sm text-secondary-600">
              Showing page {currentPage} of {totalPages} ({totalBonds} bonds)
            </div>
            <div className="flex items-center space-x-1">
              <button
                className="px-2 py-1 text-sm border rounded disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`px-2 py-1 rounded border text-sm mx-0.5 ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'}`}
                  onClick={() => setCurrentPage(page)}
                  disabled={page === currentPage}
                >{page}</button>
              ))}
              <button
                className="px-2 py-1 text-sm border rounded disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >Next</button>
            </div>
          </div>
        </div>

      </div>

      {/* Approval success is now shown via toast; modal removed */}
      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-red-700">{errorModal.title || 'Error'}</h3>
            <p className="mb-4 text-sm text-gray-700 whitespace-pre-line">{errorModal.message}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setErrorModal(null)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-800 bg-gray-200 rounded-md hover:bg-gray-300"
              >Close</button>
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Confirm Approval</h3>
            <p className="mb-4 text-sm text-gray-700">
              Are you sure you want to approve L{confirmModal.level === 'L1' ? '1' : '2'} for
              {' '}<span className="font-medium">{confirmModal.bond?.bondName}</span>?
            </p>
            <div className="p-3 mb-4 text-xs text-gray-600 rounded bg-gray-50">
              <div><span className="font-semibold">ISIN:</span> {confirmModal.bond?.isin || '-'} </div>
              <div><span className="font-semibold">Issue Amount:</span> RM {formatCurrency(toNumberSafe(confirmModal.bond?.issueAmount))}</div>
              <div><span className="font-semibold">Current Status:</span> {confirmModal.bond?.status}</div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmModal(null)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-800 bg-gray-200 rounded-md hover:bg-gray-300"
              >Cancel</button>
              <button
                onClick={async () => {
                  const { bond, level } = confirmModal;
                  setConfirmModal(null);
                  await handleInternalApprove(bond, level);
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-xl">
            <div className="flex items-start mb-4">
              <AlertCircle className="flex-shrink-0 w-6 h-6 mr-3 text-error-600" />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">Delete Bond Application</h3>
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete{' '}
                  <span className="font-medium">{deleteModal.bond?.bondName}</span>?
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="p-3 mb-4 text-xs text-gray-600 rounded bg-gray-50">
              <div><span className="font-semibold">ISIN:</span> {deleteModal.bond?.isin || '-'}</div>
              <div><span className="font-semibold">Issue Amount:</span> RM {formatCurrency(toNumberSafe(deleteModal.bond?.issueAmount))}</div>
              <div><span className="font-semibold">Status:</span> {deleteModal.bond?.status}</div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setDeleteModal(null)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-800 bg-gray-200 rounded-md hover:bg-gray-300"
              >Cancel</button>
              <button
                onClick={() => handleDeleteBond(deleteModal.bond)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md bg-error-600 hover:bg-error-700"
              >Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {rejectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Reject Bond</h3>
            <p className="mb-4 text-sm text-gray-700">
              Please provide a reason for rejecting bond <span className="font-medium">{rejectionModal.bond.bondName}</span> (L{rejectionModal.level === 'L1' ? '1' : '2'}).
            </p>
            <textarea
              className="w-full mb-4 form-input"
              rows={3}
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => { setRejectionModal(null); setRejectionReason(''); }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-800 bg-gray-200 rounded-md hover:bg-gray-300"
              >Cancel</button>
              <button
                onClick={async () => {
                  if (!rejectionReason.trim()) return;
                  try {
                    await bondsAPI.internalRejectBond(rejectionModal.bond._id, { rejectionReason });
                    toast.success(`Bond rejected at ${rejectionModal.level}.`);
                    setRejectionModal(null);
                    setRejectionReason('');
                    queryClient.invalidateQueries({ queryKey: ['bonds'] });
                  } catch (e) {
                    setErrorModal({ title: 'Rejection Failed', message: e.response?.data?.message || 'Reject failed' });
                    setRejectionModal(null);
                  }
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                disabled={!rejectionReason.trim()}
              >Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BondManagement; 